import * as React from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { useTheme } from '@emotion/react';
import { Draft, IconButton, Inline, Text, useToast } from '@resi-media/resi-ui';
import copy from 'copy-to-clipboard';
import { TrackEventName, TrackEventProp } from '@studio/constants/tracking-constants';
import { usePrefix } from '@studio/hooks/use-prefix';
import { useTrackSegment } from '@studio/hooks/use-track-segment';
import { IconWrapper } from '@studio/styles/global';

type _Props = {
  dataTestId?: string;
  dataTrackId?: string;
  iconButtonProps?: Partial<IconButton.Props>;
  isIconButton?: boolean;
  name?: string;
  onClick?: () => void;
  text: string;
};

const CopyToClipboard = ({
  dataTestId,
  dataTrackId,
  isIconButton = true,
  iconButtonProps = {},
  name,
  onClick,
  text,
}: _Props) => {
  const { commonT, prefixNS } = usePrefix('components:', 'copyToClipboard');
  const { trigger } = useToast();
  const theme = useTheme();
  const track = useTrackSegment();

  const handleCopyToClipboard = () => {
    copy(text);
    const toastMsg = name ? prefixNS('withName', { name }) : prefixNS('generic');
    trigger({ content: toastMsg });
    track({
      eventName: TrackEventName.CLIPBOARD_COPY,
      eventProps: {
        [TrackEventProp.COPY_TYPE]: name,
      },
    });

    onClick?.();
  };

  return isIconButton ? (
    <IconWrapper>
      <IconButton
        dataTestId="icon-button"
        onClick={handleCopyToClipboard}
        sizeVariant="s"
        {...iconButtonProps}
        label={iconButtonProps.label ?? commonT('copy')}
      >
        <CopyOutlined
          data-testid={dataTestId}
          data-trackid={dataTrackId}
          style={{ color: theme.palette.primary.main }}
          title={commonT('copy')}
        />
      </IconButton>
    </IconWrapper>
  ) : (
    <Draft.Button
      dataTestId="button"
      label={commonT('copy')}
      onClick={handleCopyToClipboard}
      sizeVariant="m"
      style={{ width: theme.typography.pxToRem(125) }}
      variant="outlined"
    >
      <Inline alignItems="center">
        <CopyOutlined style={{ color: theme.palette.primary.main }} />
        <Text colorVariant="primary">{commonT('copy')}</Text>
      </Inline>
    </Draft.Button>
  );
};

CopyToClipboard.displayName = 'CopyToClipboard';

export default CopyToClipboard;
